import { useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Plus,
  Loader2,
  Megaphone,
  Trash2,
  Play,
  Pause,
  Archive,
  Mail,
  MousePointerClick,
  UserX,
} from 'lucide-react';
import { leadCampaignsAPI, leadSegmentsAPI } from '../utils/api';
import {
  CAMPAIGN_STATES,
  CAMPAIGN_STATE_LABEL,
  CampaignState,
} from '../types/leadCampaign';
import LeadCampaignStateBadge from '../components/lead/LeadCampaignStateBadge';
import ConfirmModal from '../components/ConfirmModal';
import { usePermissions } from '../hooks/usePermissions';

function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('it-IT');
}

function pct(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

export default function LeadCampaigns() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission('leads:config:edit');
  const [page, setPage] = useState(1);
  const [stateFilter, setStateFilter] = useState<CampaignState | ''>('');
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['campaigns', { page, stateFilter }],
    queryFn: async () => {
      const res = await leadCampaignsAPI.getCampaigns({
        page,
        limit: 20,
        state: stateFilter || undefined,
      });
      return res.data;
    },
  });

  // Fetch segments once for name lookup
  const { data: segments } = useQuery({
    queryKey: ['segments-all'],
    queryFn: async () => {
      const res = await leadSegmentsAPI.getSegments({ limit: 200 });
      return res.data.data;
    },
    staleTime: 60_000,
  });

  const campaigns = data?.data || [];
  const pagination = data?.pagination;

  // Parallel metrics fetch (one per campaign)
  const metricsQueries = useQueries({
    queries: campaigns.map((c) => ({
      queryKey: ['campaign-metrics', c.id],
      queryFn: async () => {
        const res = await leadCampaignsAPI.getMetrics(c.id);
        return res.data;
      },
      staleTime: 60_000,
    })),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => leadCampaignsAPI.deleteCampaign(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      toast.success('Campagna eliminata');
      setDeleteTarget(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    },
  });

  const segmentNameById = (id: string) =>
    segments?.find((s) => s.id === id)?.name || '—';

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Campagne Lead</h1>
          <p className="text-gray-600">
            Sequenze drip via email su segmenti — tracking open/click/unsubscribe
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={stateFilter}
            onChange={(e) => {
              setStateFilter(e.target.value as CampaignState | '');
              setPage(1);
            }}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
          >
            <option value="">Tutti gli stati</option>
            {CAMPAIGN_STATES.map((s) => (
              <option key={s} value={s}>
                {CAMPAIGN_STATE_LABEL[s]}
              </option>
            ))}
          </select>
          {canEdit && (
            <button
              onClick={() => navigate('/leads/campaigns/new')}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="h-5 w-5" />
              Nuova campagna
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : campaigns.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Megaphone className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>Nessuna campagna</p>
            {canEdit && (
              <button
                onClick={() => navigate('/leads/campaigns/new')}
                className="mt-4 inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                <Plus className="h-4 w-4" />
                Crea la prima campagna
              </button>
            )}
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nome
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Segmento
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stato
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="inline-flex items-center gap-1">
                    <Mail className="h-3.5 w-3.5" />
                    Sent
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Open rate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Click rate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Avviata
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Azioni
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {campaigns.map((c, idx) => {
                const m = metricsQueries[idx]?.data;
                return (
                  <tr
                    key={c.id}
                    onClick={() => navigate(`/leads/campaigns/${c.id}`)}
                    className="hover:bg-gray-50 cursor-pointer"
                  >
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{c.name}</div>
                      {c.description && (
                        <div className="text-xs text-gray-500 truncate max-w-xs">
                          {c.description}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {segmentNameById(c.segment_id)}
                    </td>
                    <td className="px-6 py-4">
                      <LeadCampaignStateBadge state={c.state} />
                    </td>
                    <td className="px-6 py-4 text-sm font-semibold text-gray-900 tabular-nums">
                      {m?.sent ?? '—'}
                    </td>
                    <td className="px-6 py-4 text-sm tabular-nums">
                      {m ? (
                        <span className="inline-flex items-center gap-1">
                          <Mail className="h-3.5 w-3.5 text-blue-500" />
                          {pct(m.open_rate)}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm tabular-nums">
                      {m ? (
                        <span className="inline-flex items-center gap-1">
                          <MousePointerClick className="h-3.5 w-3.5 text-emerald-500" />
                          {pct(m.click_rate)}
                          {m.unsubscribed > 0 && (
                            <span className="ml-2 inline-flex items-center gap-1 text-xs text-red-600">
                              <UserX className="h-3 w-3" />
                              {m.unsubscribed}
                            </span>
                          )}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatDate(c.started_at)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="inline-flex items-center gap-1">
                        {c.state === 'draft' && (
                          <Play className="h-3.5 w-3.5 text-gray-300" aria-hidden />
                        )}
                        {c.state === 'active' && (
                          <Pause className="h-3.5 w-3.5 text-amber-400" aria-hidden />
                        )}
                        {c.state === 'archived' && (
                          <Archive className="h-3.5 w-3.5 text-gray-400" aria-hidden />
                        )}
                        {canEdit && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteTarget({ id: c.id, name: c.name });
                            }}
                            className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                            title="Elimina"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {pagination && pagination.pages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="text-sm text-gray-700">
              Pagina {pagination.page} di {pagination.pages} ({pagination.total} campagne)
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
              >
                Precedente
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page >= pagination.pages}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
              >
                Successivo
              </button>
            </div>
          </div>
        )}
      </div>

      {deleteTarget && (
        <ConfirmModal
          isOpen={!!deleteTarget}
          title="Elimina campagna"
          message={`Eliminare la campagna "${deleteTarget.name}"? La cronologia delivery resta nel database (audit), ma la campagna non sarà più visibile.`}
          confirmLabel="Elimina"
          onConfirm={() => deleteMutation.mutate(deleteTarget.id)}
          onClose={() => setDeleteTarget(null)}
          isLoading={deleteMutation.isPending}
          variant="danger"
        />
      )}
    </div>
  );
}
